import { ClickAwayListener, Drawer, Hidden, IconButton, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { IconInstructions } from "src/common/icons";
import Button from "./button";
import { IInstructionsMap } from "./intructionsProvider";

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: "0 16px",
    position: "absolute" as "absolute",
    right: 0,
    textAlign: "right" as "right",
    top: 75,
    [theme.breakpoints.up("lg")]: {
      position: "sticky" as "sticky",
      right: "auto" as "auto"
    }
  },
  drawerPaper: {
    padding: "0",
    width: "320px"
  },
  header: {
    "& .icon-instructions": {
      fontSize: "1.8rem",
      marginRight: 12
    },
    alignItems: "center" as "center",
    background: "#ffffff",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    display: "flex",
    height: 48,
    justifyContent: "space-between" as "space-between",
    lineHeight: "48px",
    margin: "0 -28px 12px",
    padding: "0 28px",
    position: "sticky" as "sticky",
    top: 56,
    [theme.breakpoints.up("sm")]: {
      top: 64
    }
  },
  headerLabel: {
    color: "#707070",
    fontSize: "1.6rem"
  },
  inner: {
    padding: "56px 28px 0",
    [theme.breakpoints.up("sm")]: {
      padding: "64px 28px 0"
    }
  },
  instructions: {
    "& h2": {
      color: "#707070",
      fontSize: "2rem",
      lineHeight: "2.7rem",
      margin: "24px 0 8px"
    },
    "& p": {
      lineHeight: "2.1rem",
      margin: "0 0 21px"
    },
    color: "#4d4d4d",
    fontSize: "1.6rem"
  }
}));

interface IOwnProps {
  isTranslationKey: boolean;
  translationKey?: string;
  instructionsMap: IInstructionsMap;
}

interface IOldState {
  prevTranslationKey?: string;
  prevTimeout?: NodeJS.Timeout;
}

interface IKeyStore {
  [s: string]: boolean;
}

const TIMEOUT = 300;

// Will store previous values;
function usePrevious(value: IOldState) {
  const ref = React.useRef<IOldState>();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current || {};
}

const Instructions = ({ translationKey, isTranslationKey, instructionsMap: { value: instructionList } }: IOwnProps) => {
  const {
    i18n: { language: lng }
  } = useTranslation();
  const classes = useStyles();
  const isWideScreen = document.documentElement.clientWidth >= 960;
  const { t } = useTranslation();
  const uiLang = localStorage.getItem("selectedLanguage")?.toUpperCase() || "EN";
  const [open, setOpen] = React.useState(false);
  const [currentTimeout, setCurrentTimeout] = React.useState<NodeJS.Timeout>();
  // Used to store the previous state of instruction (open/closed)
  const [keyStore, setKeyStore] = React.useState<IKeyStore>({});

  const { prevTranslationKey = null, prevTimeout = null } = usePrevious({
    prevTimeout: currentTimeout,
    prevTranslationKey: translationKey
  });

  const scheduleUpdate = React.useCallback(
    (newState: boolean) => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }
      const timeout = setTimeout(() => {
        setOpen(newState);
      }, TIMEOUT);

      setCurrentTimeout(timeout);

      if (translationKey) {
        setKeyStore({
          ...keyStore,
          [translationKey]: newState
        });
      }
    },
    [setOpen, translationKey, prevTimeout, setCurrentTimeout]
  );
  React.useEffect(() => {
    if (translationKey === prevTranslationKey) {
      return;
    } else {
      if (translationKey) {
        const existingState = keyStore[translationKey];
        if (typeof existingState === "undefined") {
          scheduleUpdate(isWideScreen);
        } else {
          scheduleUpdate(existingState);
        }
      } else {
        scheduleUpdate(false);
      }
    }
  }, [translationKey, prevTranslationKey, isWideScreen, keyStore]);

  const handleClickAway = React.useCallback(
    (e: React.MouseEvent<Document, MouseEvent>) => {
      if (open) {
        const target = (e.target as HTMLElement).closest("a, button") as HTMLAnchorElement;
        if (!target) {
          scheduleUpdate(false);
        }
      }
    },
    [scheduleUpdate, open]
  );

  const handleOpen = React.useCallback(() => scheduleUpdate(true), [scheduleUpdate]);
  const handleClose = React.useCallback(() => scheduleUpdate(false), [scheduleUpdate]);

  const instructionToDisplay = React.useMemo(() => {
    if (translationKey) {
      const defaultInstruction = instructionList[uiLang] === "" ? instructionList.EN : instructionList[uiLang];
      const displayInstruction = isTranslationKey ? t(translationKey) : defaultInstruction;
      return displayInstruction;
    } else {
      return null;
    }
  }, [translationKey, isTranslationKey, lng, instructionList]);

  if (!translationKey) {
    return null;
  }

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Drawer anchor="right" variant="persistent" classes={{ paper: classes.drawerPaper }} open={open}>
          <div className={classes.inner}>
            <div className={classes.header}>
              <div>
                <Typography color="primary" display="inline">
                  <IconInstructions />
                </Typography>
                <Typography className={classes.headerLabel} display="inline">
                  {t("feedback.instructions.title")}
                </Typography>
              </div>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </div>
            <div
              className={classes.instructions}
              dangerouslySetInnerHTML={{
                /**
                 * Translation keys can be a translation key
                 * or a html string
                 */
                __html: instructionToDisplay || ""
              }}
            />
          </div>
        </Drawer>
      </ClickAwayListener>
      <div className={classes.container}>
        <Button onClick={handleOpen}>
          <Typography color="primary">
            <IconInstructions />
          </Typography>
          <Hidden smDown>{t("feedback.instructions.title")}</Hidden>
        </Button>
      </div>
    </>
  );
};

export default React.memo(Instructions);
